import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { ViewMode, MasonryBreakpointCols, MasonryBreakpointColsList } from '@/config/constants';
import { MY_FEATURE_FLAGS } from '@/graphql/queries';
import { MyFeatureFlagsResponse } from '@/graphql/types';
import { useLazyQuery, useQuery } from '@apollo/client';

type ViewModeContextType = {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  masonryBreakpointCols: typeof MasonryBreakpointCols | typeof MasonryBreakpointColsList;
  isViewModeFeatureEnabled: boolean;
};

const ViewModeContext = createContext<ViewModeContextType>({
  viewMode: 'list',
  setViewMode: () => null,
  masonryBreakpointCols: MasonryBreakpointColsList,
  isViewModeFeatureEnabled: false,
});

type ViewModeProviderProps = {
  children: ReactNode;
};

const VIEW_MODE_FEATURE_FLAG = 'release_flyerboard_2_0';

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children }) => {
  const getViewMode = (): ViewMode => {
    const viewMode = localStorage.getItem('viewMode');
    return (viewMode as ViewMode) ?? 'list';
  };

  const [viewMode, setViewModeState] = useState<ViewMode>(getViewMode());
  const { data } = useQuery<MyFeatureFlagsResponse>(MY_FEATURE_FLAGS);
  const isViewModeFeatureEnabled = data
    ? Object.keys(data?.myFeatureFlags).includes(VIEW_MODE_FEATURE_FLAG)
    : false;

  useEffect(() => {
    if (!isViewModeFeatureEnabled) {
      setViewModeState('grid');
    } else {
      setViewModeState(getViewMode());
    }
  }, [isViewModeFeatureEnabled]);

  const setViewMode = (newViewMode: ViewMode) => {
    localStorage.setItem('viewMode', newViewMode);
    setViewModeState(newViewMode);
  };

  const masonryBreakpointCols = useMemo(() => {
    return viewMode === 'list' && isViewModeFeatureEnabled
      ? MasonryBreakpointColsList
      : MasonryBreakpointCols;
  }, [viewMode, isViewModeFeatureEnabled]);

  return (
    <ViewModeContext.Provider
      value={{ viewMode, setViewMode, masonryBreakpointCols, isViewModeFeatureEnabled }}
    >
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewModeContext = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewModeContext must be used within a ViewModeProvider');
  }
  return context;
};

export default ViewModeContext;
